// src/pages/PricingFinal.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Check, TrendingDown, ChevronDown, Shield, Users, CreditCard, ArrowRight, Clock, Award } from "lucide-react";
import {
  BILLING_PERIODS,
  PLAN_PRICING,
  getPersonalPlans,
  getBusinessPlans,
  getEnterprisePlans,
  formatPrice,
  formatSavings,
} from "../lib/pricing-new.js";
import FAQComparison from "../components/FAQComparison.jsx";
import { trackViewPricing, trackBeginCheckout, trackPlanSelected, trackBillingPeriodChange } from "../lib/analytics";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

// Objection-handling FAQ shown next to the plans. Kept honest and specific:
// the free trial is card-free (starts in the app), and Subscribe bills today.
const PRICING_FAQS = [
  {
    q: "Do I need a credit card to try SACVPN?",
    a: "No. Start a 14-day free trial with no credit card required. You only enter payment details if you decide to subscribe.",
  },
  {
    q: "What happens when my trial ends?",
    a: "Nothing automatic. The trial simply expires, and you are never charged from a trial. To keep your protection, subscribe to any plan whenever you are ready.",
  },
  {
    q: "What does Subscribe now do?",
    a: "Subscribing charges your selected plan and billing period today through Stripe, and your paid access starts right away. It is the paid path for when you are ready to commit.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from your dashboard in a couple of clicks. You keep access until the end of the period you already paid for, with no cancellation fees.",
  },
  {
    q: "What is your refund policy?",
    a: "First-time subscribers get a 30-day money-back guarantee. Full details are in our Refund and Cancellation Policy.",
    link: { to: "/refund", label: "Read the refund policy" },
  },
  {
    q: "Do you keep logs of what I do?",
    a: "We do not log your browsing activity, DNS queries, or the contents of your traffic. We process only the connection metadata needed to run the service, explained in our Privacy Policy.",
    link: { to: "/privacy", label: "Read the privacy policy" },
  },
];

const API_URL = import.meta.env.VITE_API_URL || "https://api.sacvpn.com";

/**
 * Create Stripe Checkout session
 */
async function startCheckout(planCode, billingPeriod, setBusy) {
  try {
    setBusy(true);

    const plan = PLAN_PRICING[planCode];
    const pricing = plan.pricing[billingPeriod];

    // Track checkout begin event
    trackBeginCheckout(plan.name, billingPeriod, pricing.totalCents / 100);

    const res = await fetch(`${API_URL}/api/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        plan_code: planCode,
        billing_period: billingPeriod,
        stripe_price_id: pricing.stripePriceId,
        plan_name: plan.name,
        account_type: plan.accountType,
      }),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Checkout failed: ${res.status} ${txt}`);
    }

    const { url } = await res.json();
    if (!url) throw new Error("Missing 'url' in API response");

    window.location.assign(url);
  } catch (err) {
    console.error("[pricing] checkout error", err);
    alert("We couldn't start checkout. Please try again in a moment.");
  } finally {
    setBusy(false);
  }
}

export default function PricingFinal() {
  const personalPlans = getPersonalPlans();
  const businessPlans = getBusinessPlans();
  const enterprisePlans = getEnterprisePlans();

  // Track pricing page view
  useEffect(() => {
    trackViewPricing();
  }, []);

  return (
    <>
      <Helmet>
        <title>Business VPN Pricing - Enterprise & Team Plans | SACVPN</title>
        <meta name="description" content="SACVPN business VPN pricing: Team plans from $6/device/month. Enterprise VPN with centralized management and a 14-day free trial. A combined Personal / Gaming plan is also available." />
        <meta name="keywords" content="business VPN pricing, enterprise VPN cost, team VPN plans, corporate VPN subscription, managed VPN service pricing, VPN for companies, small business VPN cost" />
        <link rel="canonical" href="https://www.sacvpn.com/pricing" />
        <meta property="og:title" content="Business VPN Pricing - Enterprise & Team Plans | SACVPN" />
        <meta property="og:description" content="SACVPN business VPN pricing: Team plans from $6/device/month. Enterprise VPN with centralized management and a 14-day free trial. A combined Personal / Gaming plan is also available." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
              Pricing
            </motion.p>

            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight max-w-4xl mx-auto">
              Simple, transparent{" "}
              <span className="text-brand-400">VPN pricing</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Start with a 14-day free trial, no credit card required. Pick the plan that fits your needs. No hidden fees, cancel anytime, and save more on longer terms.
            </motion.p>

            {/* Trial + guarantee badges */}
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap justify-center gap-x-7 gap-y-3">
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="w-4 h-4 text-brand-400" />
                <span className="text-sm">14-day free trial</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <CreditCard className="w-4 h-4 text-brand-400" />
                <span className="text-sm">30-day money-back guarantee</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Shield className="w-4 h-4 text-brand-400" />
                <span className="text-sm">No-logs policy</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Award className="w-4 h-4 text-brand-400" />
                <span className="text-sm">Veteran-owned</span>
              </div>
            </motion.div>

            {/* Primary trial CTA + quick jump links */}
            <motion.div variants={fadeUp} className="flex justify-center mt-10 gap-3 flex-wrap">
              <Link
                to="/download"
                className="px-6 py-3 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                Start 14-day free trial
              </Link>
              <a
                href="#personal"
                className="px-6 py-3 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Personal / Gaming
              </a>
              <a
                href="#business"
                className="px-6 py-3 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Business
              </a>
              <a
                href="#enterprise"
                className="px-6 py-3 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Enterprise
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">

          {/* Personal & Gaming */}
          <motion.div
            id="personal"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="scroll-mt-24"
          >
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                For individuals and gamers
              </p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                Personal / Gaming plan
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                One plan for individuals, families, and gamers. Unlimited devices on the full US WireGuard network; connect to the nearest US node and judge gaming by your in-game ping.
              </p>
            </div>
            <div className="grid gap-6 max-w-md">
              {personalPlans.map((plan) => (
                <TransparentPlanCard key={plan.code} plan={plan} />
              ))}
            </div>
          </motion.div>

          {/* Business */}
          <motion.div
            id="business"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-24 scroll-mt-24"
          >
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                For teams
              </p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                Business VPN solutions
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                Secure your team with clear device quotas. Add or revoke devices anytime from your dashboard.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {businessPlans.map((plan) => (
                <TransparentPlanCard key={plan.code} plan={plan} />
              ))}
            </div>
          </motion.div>

          {/* Enterprise */}
          <motion.div
            id="enterprise"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-24 scroll-mt-24"
          >
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Volume discounts
              </p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
                Enterprise VPN plans
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                Volume pricing for large organizations. Each user gets 3 devices, with unlimited bandwidth on dedicated servers.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enterprisePlans.map((plan) => (
                <CompactPlanCard key={plan.code} plan={plan} isEnterprise />
              ))}
            </div>
            <p className="mt-10">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-brand-400 font-medium hover:text-brand-300 transition-colors"
              >
                Need more than 10,000 users? Contact us for custom pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </p>
          </motion.div>

          {/* Trust Signals */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-24"
          >
            <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-10 md:p-12">
              <h3 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight mb-10">
                What every plan includes
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { icon: Clock, v: "14-day", l: "Free trial (self-serve plans)" },
                  { icon: CreditCard, v: "30-day", l: "Money-back guarantee" },
                  { icon: Users, v: "Unlimited", l: "Devices (personal / gaming)" },
                  { icon: Shield, v: "No-logs", l: "Privacy policy" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                      <s.icon className="w-5 h-5 text-brand-400" />
                    </div>
                    <div className="text-xl font-display font-bold text-white tracking-tight">{s.v}</div>
                    <div className="text-gray-500 mt-1 text-sm">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Pricing objection-handling FAQ */}
      <section className="py-20 bg-dark-900 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-12"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Before you decide</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Pricing questions, answered
            </h2>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-6"
          >
            {PRICING_FAQS.map((f, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-2 flex items-start gap-2.5">
                  <Shield className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                  {f.q}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.a}</p>
                {f.link && (
                  <Link
                    to={f.link.to}
                    className="inline-flex items-center gap-1.5 mt-3 text-brand-400 text-sm font-medium hover:text-brand-300 transition-colors"
                  >
                    {f.link.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <FAQComparison />
    </>
  );
}

/**
 * Transparent Plan Card - Shows all billing periods within the card
 */
function TransparentPlanCard({ plan, isEnterprise = false }) {
  const [selectedPeriod, setSelectedPeriod] = useState('twoyear'); // Default to 2-year
  const [busy, setBusy] = useState(false);

  const periods = Object.values(BILLING_PERIODS);
  const selectedPricing = plan.pricing[selectedPeriod];
  const selectedPeriodInfo = BILLING_PERIODS[selectedPeriod];

  // Get capacity text based on plan type
  const getCapacityText = () => {
    if (isEnterprise && plan.users) {
      return `${plan.users.toLocaleString()} users (${plan.devices.toLocaleString()} devices)`;
    }
    if (String(plan.devices).includes("Unlimited")) {
      return "Unlimited devices";
    }
    return `${plan.devices} devices`;
  };

  return (
    <div className="group relative flex flex-col rounded-xl border border-white/[0.08] bg-dark-900 hover:border-brand-500/30 transition-colors">
      {/* Plan Badge */}
      {plan.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-mono uppercase tracking-[0.14em] bg-brand-500 text-white rounded-md whitespace-nowrap">
          {plan.badge}
        </span>
      )}

      {/* 14-Day Free Trial Banner */}
      <div className="px-6 py-3 rounded-t-xl bg-brand-500/10 border-b border-brand-500/20">
        <div className="flex items-center justify-center gap-2 text-brand-400 font-semibold text-sm">
          <Clock className="w-4 h-4" />
          <span>14-day free trial</span>
        </div>
        <p className="text-center text-gray-500 text-xs mt-1">No credit card required to start</p>
      </div>

      <div className="p-6 flex flex-col flex-1">
        {/* Plan Header */}
        <div className="mb-5">
          <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
          <p className="mt-1 text-gray-500 text-sm">{getCapacityText()}</p>
          {isEnterprise && selectedPricing.pricePerUser && (
            <p className="text-sm text-brand-400 font-medium mt-1">
              ${selectedPricing.pricePerUser}/user/month
            </p>
          )}
        </div>

        {/* Transparent Pricing Table */}
        <div className="rounded-lg border border-white/[0.06] bg-dark-800/40 p-4 mb-4">
          <div className="text-xs font-mono uppercase tracking-[0.16em] text-gray-500 mb-3 text-center">
            Choose your billing period
          </div>

          <div className="space-y-2">
            {periods.map((period) => {
              const pricing = plan.pricing[period.id];
              const isSelected = selectedPeriod === period.id;
              const savings = pricing.savings || 0;

              return (
                <button
                  key={period.id}
                  onClick={() => {
                    setSelectedPeriod(period.id);
                    trackBillingPeriodChange(period.name);
                  }}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg transition-colors
                    ${isSelected
                      ? 'bg-brand-500/10 border border-brand-500/40'
                      : 'bg-dark-900 border border-white/[0.06] hover:border-white/15'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                          {period.name}
                        </span>
                        {period.discount > 0 && (
                          <span className={`
                            text-xs font-mono px-2 py-0.5 rounded-md
                            ${isSelected ? 'bg-brand-500/20 text-brand-300' : 'bg-white/5 text-gray-400'}
                          `}>
                            Save {period.discount}%
                          </span>
                        )}
                      </div>
                      <div className="text-xs mt-0.5 text-gray-500">
                        {period.id === 'monthly'
                          ? 'Billed monthly'
                          : `${formatPrice(pricing.totalPrice)} billed ${period.interval}`
                        }
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${isSelected ? 'text-brand-400' : 'text-white'}`}>
                        {formatPrice(pricing.monthlyPrice)}
                        <span className="text-sm font-normal text-gray-500">
                          /mo
                        </span>
                      </div>
                      {savings > 0 && (
                        <div className="text-xs flex items-center justify-end gap-1 text-gray-500">
                          <TrendingDown className="w-3 h-3" />
                          {formatSavings(savings)}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Plan Summary */}
        <div className="rounded-lg border border-brand-500/20 bg-brand-500/[0.06] p-4 mb-4">
          <div className="text-center">
            <div className="text-xs font-mono uppercase tracking-[0.16em] text-gray-500">You'll pay</div>
            <div className="text-3xl font-display font-bold text-white mt-1 tracking-tight">
              {formatPrice(selectedPricing.monthlyPrice)}
              <span className="text-lg text-gray-500 font-sans">/month</span>
            </div>
            {selectedPeriod !== 'monthly' && (
              <>
                <div className="text-sm text-gray-500 mt-1">
                  {formatPrice(selectedPricing.totalPrice)} billed {selectedPeriodInfo.interval}
                </div>
                {selectedPricing.savings > 0 && (
                  <div className="text-sm font-semibold text-brand-400 mt-1">
                    {formatSavings(selectedPricing.savings)} vs monthly billing
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Features List */}
        <ul className="space-y-2.5 flex-1 mb-6">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start text-sm text-gray-400">
              <Check className="w-4 h-4 text-brand-400 mr-2.5 flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA Buttons: free trial is the primary action; paid subscribe is secondary */}
        <div>
          <Link
            to="/download"
            className="block w-full text-center py-3 font-semibold bg-brand-500 text-white rounded-lg hover:bg-brand-400 transition-colors"
          >
            Start 14-day free trial
          </Link>
          <p className="text-center text-xs text-gray-500 mt-2">No credit card required</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => startCheckout(plan.code, selectedPeriod, setBusy)}
            className="w-full text-center disabled:opacity-60 mt-3 py-3 font-semibold border border-white/15 text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            {busy ? "Redirecting..." : "Subscribe now"}
          </button>
          <p className="text-center text-xs text-gray-500 mt-2">
            Billed {selectedPeriodInfo.interval === "month" ? "monthly" : `every ${selectedPeriodInfo.interval}`}. 30-day money-back guarantee.
          </p>
        </div>

        {/* Sign in link */}
        <div className="mt-3 text-center">
          <Link to="/login" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact Plan Card - Shows recommended 2-year price with accordion for other options
 */
function CompactPlanCard({ plan, isEnterprise = false }) {
  const [showAllPeriods, setShowAllPeriods] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('twoyear'); // Default to 2-year (recommended)
  const [busy, setBusy] = useState(false);

  const periods = Object.values(BILLING_PERIODS);
  const selectedPricing = plan.pricing[selectedPeriod];
  const selectedPeriodInfo = BILLING_PERIODS[selectedPeriod];

  // Get capacity text
  const getCapacityText = () => {
    if (isEnterprise && plan.users) {
      return `${plan.users.toLocaleString()} users (${plan.devices.toLocaleString()} devices)`;
    }
    if (String(plan.devices).includes("Unlimited")) {
      return "Unlimited devices";
    }
    return `${plan.devices} devices`;
  };

  return (
    <div className="group relative flex flex-col rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors">
      {/* Plan Badge */}
      {plan.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-mono uppercase tracking-[0.14em] bg-brand-500 text-white rounded-md whitespace-nowrap">
          {plan.badge}
        </span>
      )}

      {/* Plan Header */}
      <div className="mb-5">
        <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
        <p className="mt-1 text-gray-500 text-sm">{getCapacityText()}</p>
      </div>

      {/* Main Price Display - Shows selected period */}
      <div className="rounded-lg border border-brand-500/20 bg-brand-500/[0.06] p-4 mb-4">
        <div className="text-xs font-mono uppercase tracking-[0.14em] text-gray-500 mb-1">
          {selectedPeriodInfo.name} plan
          {selectedPeriodInfo.discount > 0 && (
            <span className="ml-2 text-brand-400">Save {selectedPeriodInfo.discount}%</span>
          )}
        </div>
        <div className="text-3xl font-display font-bold text-white tracking-tight">
          {formatPrice(selectedPricing.monthlyPrice)}
          <span className="text-lg text-gray-500 font-sans">/mo</span>
        </div>
        {isEnterprise && selectedPricing.pricePerUser && (
          <div className="text-sm text-brand-400 font-medium mt-1">
            ${selectedPricing.pricePerUser}/user/month
          </div>
        )}
        {selectedPeriod !== 'monthly' && (
          <div className="text-sm text-gray-500 mt-1">
            {formatPrice(selectedPricing.totalPrice)} billed {selectedPeriodInfo.interval}
          </div>
        )}
        {selectedPricing.savings > 0 && (
          <div className="text-sm font-semibold text-brand-400 mt-1">
            {formatSavings(selectedPricing.savings)}
          </div>
        )}
      </div>

      {/* Billing Period Accordion */}
      <div className="mb-4">
        <button
          onClick={() => setShowAllPeriods(!showAllPeriods)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
        >
          <span>Change billing period</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showAllPeriods ? 'rotate-180' : ''}`} />
        </button>

        {showAllPeriods && (
          <div className="mt-2 space-y-1 border-t border-white/[0.06] pt-2">
            {periods.map((period) => {
              const pricing = plan.pricing[period.id];
              const isSelected = selectedPeriod === period.id;

              return (
                <button
                  key={period.id}
                  onClick={() => {
                    setSelectedPeriod(period.id);
                    setShowAllPeriods(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                    isSelected
                      ? 'bg-brand-500/10 border border-brand-500/40'
                      : 'hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className={`font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>{period.name}</span>
                      {period.discount > 0 && (
                        <span className="ml-2 text-xs text-gray-500">
                          -{period.discount}%
                        </span>
                      )}
                    </div>
                    <div className={`font-semibold ${isSelected ? 'text-brand-400' : 'text-white'}`}>
                      {formatPrice(pricing.monthlyPrice)}/mo
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Features List */}
      <ul className="space-y-2.5 flex-1 mb-6">
        {plan.features.slice(0, 5).map((feature, i) => (
          <li key={i} className="flex items-start text-sm text-gray-400">
            <Check className="w-4 h-4 text-brand-400 mr-2.5 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
        {plan.features.length > 5 && (
          <li className="text-xs text-gray-500 pl-[26px]">
            +{plan.features.length - 5} more features
          </li>
        )}
      </ul>

      {/* CTA Buttons */}
      <button
        type="button"
        disabled={busy}
        onClick={() => startCheckout(plan.code, selectedPeriod, setBusy)}
        className="w-full text-center disabled:opacity-60 py-3 font-semibold bg-brand-500 text-white rounded-lg hover:bg-brand-400 transition-colors"
      >
        {busy ? "Redirecting..." : `Get ${plan.name}`}
      </button>
      <p className="text-center text-xs text-gray-500 mt-2">
        Billed {selectedPeriodInfo.interval === "month" ? "monthly" : `every ${selectedPeriodInfo.interval}`}
      </p>
      <Link
        to="/contact"
        className="block w-full text-center mt-3 py-3 font-semibold border border-white/15 text-white rounded-lg hover:bg-white/5 transition-colors"
      >
        Talk to sales
      </Link>

      {/* Sign in link */}
      <div className="mt-3 text-center">
        <Link to="/login" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
          Already have an account? Sign in
        </Link>
      </div>
    </div>
  );
}
